'use client';

import AuthProviderButton from '@/features/auth/components/AuthProviderButton';
import GoogleAuthButton from '@/features/auth/google/GoogleAuthButton';
import { useGoogleAuth } from '@/features/auth/google/useGoogleAuth';
import { useOAuthPopupAuth } from '@/features/auth/oauth/useOAuthPopupAuth';
import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { getSafeAuthClientMessage } from '@/features/auth/utils/getSafeAuthClientMessage';
import { FormField, FormGrid } from '@/features/forms/FormPrimitives';
import Message from '@/features/primitives/Message';
import { clientFetch } from '@/lib/api/clientFetch';
import { motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { ChevronRight, Discord } from 'react-bootstrap-icons';

function readRegisterMessage(messages, path, fallback = '') {
  const resolved = String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((current, segment) => current?.[segment], messages?.registerScreen);

  return typeof resolved === 'string' ? resolved : fallback;
}

export default function RegisterForm({
  redirectTarget = '/account',
  messages = {},
  title = 'Sign Up',
  brandMarkAlt = 'Register',
}) {
  const router = useRouter();
  const targetPath = redirectTarget || '/account';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reduceMotion = useReducedMotion();
  const registerMessage = (path, fallback = '') =>
    readRegisterMessage(messages, path, fallback);
  const registerErrors = {
    generic: registerMessage('errors.generic', 'Unable to create account.'),
    accountExists: registerMessage(
      'errors.accountExists',
      'An account already exists for this email.',
    ),
    tooManyAttempts: registerMessage(
      'errors.tooManyAttempts',
      'Too many attempts. Please wait a moment and try again.',
    ),
    popupBlocked: registerMessage(
      'errors.popupBlocked',
      'Authentication popup was blocked. Please allow popups and try again.',
    ),
    passwordTooShort: registerMessage(
      'errors.passwordTooShort',
      'Password must be at least 6 characters.',
    ),
    passwordsDoNotMatch: registerMessage(
      'errors.passwordsDoNotMatch',
      'Passwords do not match.',
    ),
    googleUnavailable: registerMessage(
      'errors.googleUnavailable',
      'Google sign-in is unavailable right now.',
    ),
    googleLoadFailed: registerMessage(
      'errors.googleLoadFailed',
      'Google sign-in could not load. Please try again.',
    ),
    googleIncomplete: registerMessage(
      'errors.googleIncomplete',
      'Google sign-in did not complete. Please try again.',
    ),
    googleFailed: registerMessage(
      'errors.googleFailed',
      'Google sign-in failed. Please try again.',
    ),
    discordUnavailable: registerMessage(
      'errors.discordUnavailable',
      'Discord sign-in is unavailable right now.',
    ),
    discordIncomplete: registerMessage(
      'errors.discordIncomplete',
      'Discord sign-in did not complete. Please try again.',
    ),
    discordFailed: registerMessage(
      'errors.discordFailed',
      'Discord sign-in failed. Please try again.',
    ),
  };
  const navigateAfterAuth = () => {
    if (typeof window !== 'undefined') {
      window.location.replace(targetPath);
      return;
    }

    router.replace(targetPath);
    router.refresh();
  };
  const handleProviderError = (fallbackMessage) => (error) =>
    setErrorMessage(
      getSafeAuthClientMessage(error, {
        fallbackMessage,
        messages: registerErrors,
      }),
    );
  const googleAuth = useGoogleAuth({
    redirectTarget,
    onError: handleProviderError(registerErrors.googleUnavailable),
    onSuccess: async () => {
      navigateAfterAuth();
    },
  });
  const discordAuth = useOAuthPopupAuth({
    provider: 'discord',
    redirectTarget,
    onError: handleProviderError(registerErrors.discordUnavailable),
    onSuccess: async () => {
      navigateAfterAuth();
    },
  });
  const isPopupAuthBusy = discordAuth.isLoading;
  // Discord is a hub-specific auth provider; only show it when the user
  // is navigating within /hub routes.
  const showDiscord = Boolean(redirectTarget?.startsWith('/hub'));

  const containerVariants = {
    hidden: {
      opacity: 0,
      y: reduceMotion ? 0 : 8,
      scale: reduceMotion ? 1 : 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' },
    },
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    if (password.length < 6) {
      setErrorMessage(registerErrors.passwordTooShort);
      setIsSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(registerErrors.passwordsDoNotMatch);
      setIsSubmitting(false);
      return;
    }

    try {
      await clientFetch('/api/users', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
        }),
      });

      navigateAfterAuth();
    } catch (error) {
      setErrorMessage(
        getSafeAuthClientMessage(error, {
          fallbackMessage: registerErrors.generic,
          messages: registerErrors,
        }),
      );
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    router.replace('/shop');
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <Modal
      show
      onHide={handleClose}
      centered
      animation
      backdrop="static"
      backdropClassName="auth-glass-backdrop"
      dialogClassName="auth-glass-modal register-glass-modal"
      contentClassName="auth-glass-modal__content"
      aria-labelledby="register-legend"
    >
      <Modal.Body>
        <button
          type="button"
          className="auth-glass-modal__close"
          onClick={handleClose}
          aria-label={registerMessage('closeLabel', 'Close')}
        />

        <div className="auth-surface-wrapper subtotal-card-wrapper">
          <div className="checkout-summary checkout-summary--section auth-surface-card">
            <div className="auth-surface-body">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="auth-modal auth-glass-modal__inner"
              >
                <form className="auth-form" onSubmit={handleSubmit} noValidate>
                  <fieldset
                    id="register-group"
                    aria-labelledby="register-legend"
                  >
                    <div className="auth-brand-mark" aria-hidden="true">
                      <img
                        src="/icons/register.png"
                        alt={brandMarkAlt}
                        width="44"
                        height="44"
                        className="profile-avatar-img"
                      />
                    </div>

                    <legend id="register-legend" className="form-legend mt-n2">
                      {title}
                    </legend>

                    <div
                      className={`auth-provider-grid${showDiscord ? '' : ' auth-provider-grid--single'}`}
                    >
                      <GoogleAuthButton
                        onClick={googleAuth.signIn}
                        disabled={
                          isSubmitting ||
                          googleAuth.isLoading ||
                          isPopupAuthBusy
                        }
                        label={registerMessage(
                          'googleCta',
                          'Continue with Google',
                        )}
                      />

                      {showDiscord && (
                        <AuthProviderButton
                          provider="discord"
                          onClick={discordAuth.signIn}
                          disabled={isSubmitting || isPopupAuthBusy}
                          label={registerMessage(
                            'discordCta',
                            'Continue with Discord',
                          )}
                          icon={<Discord />}
                        />
                      )}
                    </div>

                    <div className="form-divider">
                      <span>{registerMessage('divider', 'or')}</span>
                    </div>

                    <FormGrid className="auth-form__grid">
                      <FormField
                        label={registerMessage('form.name.label', 'Name')}
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        required
                        hideLabel
                        placeholder={registerMessage(
                          'form.name.placeholder',
                          'Name',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />

                      <FormField
                        label={registerMessage('form.email.label', 'Email')}
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        hideLabel
                        placeholder={registerMessage(
                          'form.email.placeholder',
                          'Email',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />

                      <FormField
                        label={registerMessage(
                          'form.password.label',
                          'Password',
                        )}
                        name="password"
                        type="password"
                        passwordToggle
                        showPasswordLabel={registerMessage(
                          'form.password.showToggle',
                          'Show password',
                        )}
                        hidePasswordLabel={registerMessage(
                          'form.password.hideToggle',
                          'Hide password',
                        )}
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        minLength={6}
                        hideLabel
                        placeholder={registerMessage(
                          'form.password.placeholder',
                          'Password',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />

                      <FormField
                        label={registerMessage(
                          'form.confirmPassword.label',
                          'Confirm Password',
                        )}
                        name="confirmPassword"
                        type="password"
                        passwordToggle
                        showPasswordLabel={registerMessage(
                          'form.confirmPassword.showToggle',
                          'Show password',
                        )}
                        hidePasswordLabel={registerMessage(
                          'form.confirmPassword.hideToggle',
                          'Hide password',
                        )}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) =>
                          setConfirmPassword(event.target.value)
                        }
                        required
                        minLength={6}
                        hideLabel
                        placeholder={registerMessage(
                          'form.confirmPassword.placeholder',
                          'Confirm password',
                        )}
                        showRequiredAsterisk
                        asteriskPosition="left"
                      />
                    </FormGrid>

                    {errorMessage ? (
                      <Message tone="error" className="mt-3">
                        {errorMessage}
                      </Message>
                    ) : null}

                    <div className="d-grid mt-3">
                      <button
                        type="submit"
                        className="app-button app-button--primary register-cta gap-1"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? registerMessage(
                              'form.submitting',
                              'Creating Account...',
                            )
                          : registerMessage('form.submit', 'Create my account')}
                        {isSubmitting ? (
                          <span
                            className="spinner-border spinner-border-sm ms-1"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronRight size={14} strokeWidth={1.5} />
                        )}
                      </button>
                    </div>

                    <p className="microcopy small mt-2">
                      {registerMessage(
                        'microcopy',
                        'You can change this later.',
                      )}
                    </p>
                  </fieldset>
                </form>

                <p className="login-text">
                  <span>
                    {registerMessage(
                      'form.signInPrompt',
                      'Already have an account?',
                    )}
                  </span>{' '}
                  <Link
                    href={buildAuthHref('/login', redirectTarget)}
                    className="custom-btn-link p-0"
                  >
                    {registerMessage('form.signInLink', 'Login')}
                  </Link>
                  .
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
